import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Package, ListOrdered, Store, LogOut, AlertTriangle, KeyRound, Wrench } from "lucide-react";

const NAV = [
  { to: "/", label: "Schermata di lavoro", icon: Package, testid: "nav-lavorazione" },
  { to: "/ordini", label: "Tutti gli ordini", icon: ListOrdered, testid: "nav-ordini" },
  { to: "/store", label: "Store collegati", icon: Store, testid: "nav-store" },
  { to: "/manutenzione", label: "Manutenzione", icon: Wrench, testid: "nav-manutenzione" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [simulation, setSimulation] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  useEffect(() => {
    API.get("/config")
      .then((r) => setSimulation(!!r.data.simulation_mode))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-slate-800 bg-slate-900 text-white shadow-md">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-3">
          <div className="flex items-center gap-8">
            <span className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "Barlow, sans-serif" }}>
              Dashboard Spedizioni
            </span>
            <nav className="flex items-center gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                      isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`
                  }
                >
                  <n.icon size={16} />
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400" data-testid="logged-user-email">{user?.email}</span>
            <button
              data-testid="change-password-btn"
              onClick={() => setPwdOpen(true)}
              className="flex items-center gap-2 rounded-md border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-800"
            >
              <KeyRound size={16} /> Cambia password
            </button>
            <button
              data-testid="logout-btn"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="flex items-center gap-2 rounded-md border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-800"
            >
              <LogOut size={16} /> Esci
            </button>
          </div>
        </div>
      </header>
      {simulation && (
        <div
          data-testid="simulation-banner"
          className="flex items-center justify-center gap-2 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-900"
        >
          <AlertTriangle size={16} />
          MODALITÀ SIMULATA ATTIVA — nessuna chiamata reale a Shopify o Spediamo Pro, nessun costo. Si spegne con SIMULATION_MODE=false.
        </div>
      )}
      <main className="mx-auto max-w-[1500px] px-6 py-6">
        <Outlet />
      </main>
      {pwdOpen && <ChangePasswordModal onClose={() => setPwdOpen(false)} />}
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("La nuova password e la conferma non coincidono");
      return;
    }
    setSaving(true);
    try {
      await API.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password aggiornata. Le altre sessioni sono state invalidate.");
      onClose();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-testid="change-password-modal">
      <form onSubmit={save} className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <h2 className="mb-1 text-xl font-bold">Cambia password</h2>
        <p className="mb-4 text-xs text-muted-foreground">
          Minimo 8 caratteri. Dopo il cambio, le altre sessioni aperte verranno invalidate.
        </p>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password attuale</label>
            <input
              data-testid="cp-current"
              type="password"
              required
              autoFocus
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="h-11 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nuova password</label>
            <input
              data-testid="cp-new"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="h-11 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Conferma nuova password</label>
            <input
              data-testid="cp-confirm"
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-11 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
        </div>
        {error && (
          <div data-testid="cp-error" className="mt-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800">
            {error}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            data-testid="cp-cancel"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold hover:bg-slate-100"
          >
            Annulla
          </button>
          <button
            type="submit"
            data-testid="cp-save"
            disabled={saving}
            className="rounded-lg bg-blue-600 px-6 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? "Salvataggio…" : "Aggiorna password"}
          </button>
        </div>
      </form>
    </div>
  );
}
